import { memo, Dispatch, SetStateAction, useState, useEffect } from "react";

import { useFormContext, UseFormSetValue } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import ImageDialog from "@app/components/ImageDialog";
import RenderNewUploadingImagesList from "@app/components/RenderNewUploadingImagesList";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRenderFileViewHook } from "@app/hooks/imagesRender";
import { ApiEndpoint, imagesFileFormats } from "@constants/index";
import { useSortImagesMutation } from "@modules/images/api/image.api";
import { arrayMoveImmutable } from "array-move";
import { Image } from "types";

import {
  CreateProductState,
  ExistingImagesKeys,
  FileListKeys,
  IFlowProcess,
  IProductFormData,
  OnChangeFunctionProps
} from "@pages/ProductMutation/helper/types";
import { MainLayer, ContentWrapper, VisuallyHiddenInput } from "./styled";

interface ImagesContainerProps {
  keyUploadedFileList: FileListKeys;
  keyMainImageUrl: 'mainImageUrl';
  keyExistingImages: ExistingImagesKeys;
  buttonText: string;
  buttontype?: 'text' | 'outlined' | 'contained';
  isImageViewOpen: boolean;
  imageSrcForAction: string;
  multiple: boolean;
  posibilityToMainImage: boolean;
  helperText?: string;
  setValue: UseFormSetValue<IProductFormData>;
  handleDeleteImage: (str: any) => void;
  setFlowProcess: Dispatch<SetStateAction<IFlowProcess>>;
  onChangeUpload: (props: OnChangeFunctionProps) => void;
}

const ImagesContainer = ({
  keyUploadedFileList,
  keyMainImageUrl,
  keyExistingImages,
  buttonText,
  buttontype = 'contained',
  imageSrcForAction,
  isImageViewOpen,
  multiple,
  posibilityToMainImage,
  helperText,
  setValue,
  setFlowProcess,
  onChangeUpload,
  handleDeleteImage,
}: ImagesContainerProps) => {
  const { watch, formState: { errors } } = useFormContext<IProductFormData>()
  const image = watch(keyUploadedFileList)
  const mainImageUrl = watch(keyMainImageUrl)
  const existingImages = watch(keyExistingImages)
  const [sortImages] = useSortImagesMutation()

  const [localExisting, setLocalExisting] = useState(existingImages)

  useEffect(() => {
    setLocalExisting(existingImages)
  }, [existingImages])

  const isUploadedImage = image && 'url' in image

  const previews = useRenderFileViewHook(image, localExisting)

  const onSortEnd = async (oldIndex: number, newIndex: number) => {
    try {
      const clone = JSON.parse(JSON.stringify(localExisting)) as Image[]

      const sorted = arrayMoveImmutable(clone, oldIndex, newIndex)

      const ids = sorted.map((item) => item.id)

      await sortImages({ ids }).unwrap();
      setLocalExisting(sorted)
    } catch (error) {
      console.log('Error:', error);
    }
  }

  return (
    <MainLayer>
      <ContentWrapper>
        <RenderNewUploadingImagesList<CreateProductState>
          togetherImagesLength={isUploadedImage ? 1 : previews.length}
          newPreviewsList={isUploadedImage ? [`${ApiEndpoint}/${image.url}`] : previews}
          setState={setFlowProcess}
          handleDeleteImage={handleDeleteImage}
          posibilityToMainImage={posibilityToMainImage}
          mainImageUrl={mainImageUrl}
          setMainImage={(id: string) => setValue(keyMainImageUrl, id)}
          sortable
          onSortEnd={onSortEnd}

        />
        <ImageDialog
          url={imageSrcForAction}
          isOpen={isImageViewOpen}
          onClose={() => {
            setFlowProcess((prev) => ({ ...prev, isImageViewOpen: false }))
          }}
        />
      </ContentWrapper>
      <Box color="red">
        {errors?.[keyUploadedFileList]?.message ? "Product image is required" : ""}
      </Box>
      <Box
        sx={{ mt: 1, display: 'flex', gap: 1 }}
      >
        <Button
          component="label"
          role={undefined}
          variant={buttontype}
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
        >
          <p>{ image?.length ? 'Update Files' : buttonText }</p>
          <VisuallyHiddenInput
            type="file"
            id="files"
            multiple={multiple}
            name="images"
            accept={imagesFileFormats.join(',')}
            onChange={(event) => onChangeUpload({
              event,
              multiple,
              keyExistingImages,
              keyUploadedFileList,
            })}
          />
        </Button>
      </Box>
      {helperText && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {helperText}
        </Typography>
      )}
    </MainLayer>
  )
}

export default memo(ImagesContainer)
